import React from 'react';
import { createHashRouter, Navigate, RouterProvider } from 'react-router-dom';

import { Box, Typography } from '@mui/material';

import Footer from './components/footer';
import TopNavigation from './components/top-navigation';
import { useConfig } from './ConfigContext';
import AdminPage from './pages/admin';
import DynamicPage from './pages/dynamic-page';
import { ImageObject, PageConfig } from './pages/types';
import CircularProgress from '@mui/material/CircularProgress';

export interface ComponentProps {
  [key: string]: any; // For dynamic properties
  // Common properties
  id?: string;

  // Hero/PageHeader properties
  title?: string;
  subtitle?: string;

  // TextBox properties
  texts?: Array<{
    type: string;
    contents: string;
    link?: string;
  }>;
  textBoxGap?: string;

  // Carousel/ImageGallery properties
  images?: Array<string | ImageObject>;
  rowHeight?: string;
  carouselHeight?: string | number;
  columns?: number;
  minColumns?: number;
  gap?: string;

  // TitleParagraph/Ingress properties
  paragraph?: string;

  // Iframe component properties
  iframeUrl?: string;
  iframeHeight?: string | number;
  iframeWidth?: string | number;

  // SingleImage properties
  image?: string | ImageObject;
  imageWidth?: string;
  imageHeight?: string;
  showCaption?: boolean;

  // Container properties
  container?: any;
}

export interface NavigationConfig {
  logo?: {
    url: string;
    alt: string;
    height?: string;
  };
  homeLink?: string;
  showHomeText?: boolean;
  lastItemIsButton?: boolean;
  buttonColor?: string;
}

export interface FooterConfig {
  title?: string;
  subtitle?: string;
  links?: {
    instagram?: string;
    facebook?: string;
    x?: string;
    youtube?: string;
    linkedin?: string;
  };
}

// Use the consistent PageConfig from types.ts
export interface Config {
  theme: any;
  pages: Record<string, PageConfig>;
  navigationConfig: NavigationConfig;
  footerConfig: FooterConfig;
}

function isPageConfig(obj: any): obj is PageConfig {
  return (
    obj &&
    typeof obj === 'object' &&
    'menutitle' in obj &&
    'components' in obj &&
    Array.isArray(obj.components)
  );
}

// Remove the duplicate preload functions and keep only the critical one
const preloadCriticalImages = (
  pageConfig: PageConfig,
  maxComponents = 1
): Promise<void[]> => {
  const imageUrls = new Set<string>();

  // Helper function to extract image URL from string or ImageObject
  const getImageUrl = (image: string | ImageObject): string => {
    return typeof image === 'string' ? image : image.url;
  };

  // Extract images only from the first component (hero/header usually)
  const extractCriticalImages = (components: any[], maxCount: number) => {
    const criticalComponents = components.slice(0, maxCount);

    criticalComponents.forEach((component) => {
      const props = component.props || {};
      const styles = component.styles || {};

      // Extract from component props
      if (props.image) {
        imageUrls.add(getImageUrl(props.image));
      }
      // For image arrays, only take the first image
      if (
        props.images &&
        Array.isArray(props.images) &&
        props.images.length > 0
      ) {
        imageUrls.add(getImageUrl(props.images[0]));
      }

      // Extract from component styles (background images)
      if (styles.backgroundImage) {
        imageUrls.add(getImageUrl(styles.backgroundImage));
      }

      // For containers, only check the first item
      if (
        props.container &&
        props.container.items &&
        props.container.items.length > 0
      ) {
        extractCriticalImages([props.container.items[0]], 1);
      }
    });
  };

  if (pageConfig.components && pageConfig.components.length > 0) {
    extractCriticalImages(pageConfig.components, maxComponents);
  }

  // Preload critical images
  const preloadPromises = Array.from(imageUrls).map((url) => {
    return new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => {
        console.warn(`Failed to preload critical image: ${url}`);
        resolve();
      };
      img.src = url;
    });
  });

  return Promise.all(preloadPromises);
};

const App: React.FC = () => {
  const { config } = useConfig();
  const [criticalImagesLoaded, setCriticalImagesLoaded] = React.useState(false);

  console.warn('Portfolio Generator version 1.0');

  const validPages: Record<string, PageConfig> = config?.pages
    ? Object.entries(config.pages).reduce((acc, [pageName, pageConfig]) => {
        if (isPageConfig(pageConfig)) {
          acc[pageName] = pageConfig;
        } else {
          console.error(`Invalid page configuration for: ${pageName}`);
        }
        return acc;
      }, {} as Record<string, PageConfig>)
    : {};

  // Preload only the most critical images (first component only)
  React.useEffect(() => {
    if (config?.pages && Object.keys(validPages).length > 0) {
      const homePage = validPages['Home'];

      if (homePage) {
        // Load only the first component's main image
        preloadCriticalImages(homePage, 1)
          .then(() => {
            setCriticalImagesLoaded(true);
            console.log('Critical images loaded, page ready to show');
          })
          .catch((error) => {
            console.error('Error preloading critical images:', error);
            setCriticalImagesLoaded(true);
          });
      } else {
        setCriticalImagesLoaded(true);
      }
    }
  }, [config]);

  // Show minimal loading screen
  if (!criticalImagesLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: config?.theme?.palette?.background?.default || '#ffffff',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Helper function to generate routes for all pages and subpages
  const generateRoutes = (
    pages: Record<string, PageConfig>,
    parentPath: string = ''
  ) => {
    const routes: any[] = [];

    Object.entries(pages).forEach(([pageName, pageConfig]) => {
      // Skip pages that don't have the required fields
      if (!pageConfig || !Array.isArray(pageConfig.components)) return;

      // Current page path
      const currentPath = parentPath ? `${parentPath}/${pageName}` : pageName;
      const displayPath =
        pageName === 'Home' && !parentPath ? '/' : `/${currentPath}`;

      // Add route for current page
      routes.push({
        path: displayPath,
        element: (
          <Box
            sx={{
              background: config?.theme.palette.background.default,
              backgroundImage: config?.theme.backgroundImage
                ? `url(${config.theme.backgroundImage})`
                : 'none',
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundAttachment: 'fixed',
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
            }}
          >
            <TopNavigation />
            <Box sx={{ flexGrow: 1 }}>
              <DynamicPage pageName={pageName} parentPath={parentPath} />
            </Box>
            <Footer />
          </Box>
        ),
      });

      // Generate routes for subpages if they exist
      if (pageConfig.subpages && Object.keys(pageConfig.subpages).length > 0) {
        const subRoutes = generateRoutes(pageConfig.subpages, currentPath);
        routes.push(...subRoutes);
      }
    });

    return routes;
  };

  // Generate routes for all pages and subpages
  const pageRoutes = generateRoutes(validPages);

  const router = createHashRouter([
    {
      path: '/admin',
      element: <AdminPage />,
    },
    {
      path: '/Home',
      element: <Navigate to="/" replace />,
    },
    // Add all generated page routes (including subpages)
    ...pageRoutes,
  ]);

  return <RouterProvider router={router} />;
};

export default App;
